import {gql} from '@apollo/client';
import {useMemo} from 'react';

import {useBenchlingQuery, QueryResultType} from 'graphql-client/BenchlingQuery';
import {makeReactContext} from 'lib/makeReactContext';
import {libraryDefinitionDataTypeToDataParameterFieldType} from 'procedures/parameter-library/libraryParameterDefinitionDataTypeConversion';
import {LibraryParameterDefinitionIdName} from 'procedures/parameter-library/ParameterLibraryTypes';
import {DataParameterFieldType} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';

import type {
  AllLibraryParameterDefinitionsQuery,
  AllLibraryParameterDefinitionsQueryVariables,
} from './__generated/AllLibraryParameterDefinitionsQuery';

export const ALL_LIBRARY_PARAMETER_DEFINITIONS_QUERY = gql`
  query AllLibraryParameterDefinitionsQuery($includeArchived: StrictBoolean)
  @skipAudit
  @engComponent(name: PROCEDURES) {
    allLibraryParameterDefinitions(includeArchived: $includeArchived) {
      id
      name
      dataType
    }
  }
`;

/** Library definition row from the consolidated query (id, name, and procedure field type). */
export type LibraryParameterDefinitionContextItem = LibraryParameterDefinitionIdName & {
  fieldType: DataParameterFieldType;
};

export const LibraryParameterDefinitionsContext = makeReactContext<
  Array<LibraryParameterDefinitionContextItem>
>(() => [], 'LibraryParameterDefinitionsContext');

/**
 * Fetches all (non-archived) library parameter definitions in a single query.
 */
export function useAllLibraryParameterDefinitions(): Array<LibraryParameterDefinitionContextItem> {
  const queryResult = useBenchlingQuery<
    AllLibraryParameterDefinitionsQuery,
    AllLibraryParameterDefinitionsQueryVariables
  >(ALL_LIBRARY_PARAMETER_DEFINITIONS_QUERY, {
    variables: {includeArchived: false},
  });

  return useMemo(() => {
    if (queryResult.type !== QueryResultType.SUCCESS) {
      return [];
    }

    return queryResult.data.allLibraryParameterDefinitions.map((def) => ({
      id: def.id,
      name: def.name,
      fieldType: libraryDefinitionDataTypeToDataParameterFieldType(def.dataType),
    }));
  }, [queryResult]);
}
